import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  CareerPath,
  CareerPathCompensation,
  CareerPathSampleJobs,
  CareerPathAIDisruption,
  DoubleSelectDropdown,
  DoubleSelectDropdownProps,
  NUGGET_VARIANTS,
  AppIcon,
} from '@backrs_ui/common-ui';

import { appStrings as commonAppStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { handleShareWithNavigator } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { ResourceHeader } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/ResourceHeader';
import { ResourceWrapper } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/ResourceWrapper';
import { ResourcePageSection } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/types';
import { usePageTitle } from '@backrs_ui/grow-ui/src/hooks/usePageTitle';
import { CareerPathWithScore } from '@backrs_ui/grow-ui/src/models/models';
import { getBookmarkIdForNugget } from '@backrs_ui/grow-ui/src/pages/Bookmarks/pages/bookmarks.util';
import { SEARCH_PARENT_CATEGORIES_DEFAULT } from '@backrs_ui/grow-ui/src/pages/Search/search.config';
import {
  usePostBookmarkMutation,
  useDeleteBookmarkMutation,
  useGetBookmarksQuery,
} from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { useAppSelector, useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { PagePrompt } from '@backrs_ui/grow-ui/src/redux/pagePrompts/pagePrompts';
import { useRegisterPagePrompts } from '@backrs_ui/grow-ui/src/redux/pagePrompts/useRegisterPagePrompts';
import {
  openPlaylistListStep,
  setNuggetToAdd,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';
import { findCareerPathSuggestionById } from '@backrs_ui/grow-ui/src/utils/careerPath/careerPathSuggestions';

import { Chip, SxProps, Theme } from '@mui/material';

import { useAgentContext } from './CareerPathDetailPage.agentContext';
import { AiImpact } from './molecules/AiImpact';
import { CareerPathOverview } from './molecules/CareerPathOverview';
import { EarningPotential } from './molecules/EarningPotential';
import {
  ResourceNuggetSection,
  ResourceNuggetSectionFilter,
} from './molecules/ResourceNuggetSection';
import { TrackDistribution } from './molecules/TrackDistribution';
import {
  getCareerPathCategoryName,
  getCareerPathIcon,
  getCareerPathFitColor,
  FIT_SCORE_LEVEL_MAP,
  getStrongFitCountForCategory,
  transformCareerCategories,
  transformCareerPaths,
} from './util';

const STRINGS = appStrings.modules.careerPath.detailPage;
const GROW_URL = process.env.REACT_APP_GROW_URL;

export function CareerPathDetailPage({
  careerPath,
  careerPathCompensations,
  careerPathSampleJobs,
  careerPaths,
  careerPathSuggestions,
  careerPathAIDisruption,
  responseSetId,
  isShared = false,
  isReadOnly = false,
}: {
  careerPath: CareerPath;
  careerPathCompensations: CareerPathCompensation[];
  careerPathSampleJobs: CareerPathSampleJobs;
  careerPaths: CareerPath[];
  careerPathSuggestions: CareerPathWithScore[] | undefined;
  careerPathAIDisruption: CareerPathAIDisruption;
  /** The set the fit is explained against. Omitted, the reader's own is used. */
  responseSetId?: string;
  isShared?: boolean;
  isReadOnly?: boolean;
}) {
  useAgentContext(careerPath);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const sharedSessionUUID = params.uuid;

  const { careerCategories } = useAppSelector((state) => state.careerPath);

  usePageTitle(careerPath.name);

  const { data: bookmarks } = useGetBookmarksQuery(undefined);
  const [postBookmark] = usePostBookmarkMutation();
  const [deleteBookmark] = useDeleteBookmarkMutation();

  const careerPathPrompts = useMemo<readonly PagePrompt[]>(
    () => [
      { promptButtonId: 'career_path_is_good_fit', title: 'Is this career a good fit for me?' },
      {
        promptButtonId: 'career_path_three_roles',
        title: `Three ${careerPath.name.toLowerCase()} roles for me`,
      },
      { promptButtonId: 'career_path_requirements', title: 'How to succeed' },
      { promptButtonId: 'career_path_caregiver_flexibility', title: 'Caregiver flexibility' },
      { promptButtonId: 'career_path_best_majors', title: 'Top majors' },
    ],
    [careerPath.name],
  );

  // The prompts are written in the first person, so they only belong to a reader
  // looking at their own career path.
  useRegisterPagePrompts(isShared || isReadOnly ? null : careerPathPrompts);

  // ── Bookmark / Share / Task handlers ──

  const bookmarkId = getBookmarkIdForNugget(bookmarks, careerPath?.id, 'career-path');

  const handleBookmark = async () => {
    if (careerPath?.id) {
      if (bookmarkId) {
        await deleteBookmark(bookmarkId);
      } else {
        await postBookmark({ type: 'career-path', itemId: careerPath.id });
      }
    }
  };

  const handleShare = () => {
    handleShareWithNavigator({
      title: careerPath.name,
      text: careerPath.description || '',
      url: `${GROW_URL}/career-paths/${careerPath.id}`,
    });
  };

  const handleCreateTask = () => {
    dispatch(
      setNuggetToAdd({
        id: careerPath.id,
        slug: careerPath.name || '',
        title: careerPath.name,
        description: careerPath.description || '',
        star_rating: 0,
        value_rating: 0,
        is_browsable: false,
        variant: NUGGET_VARIANTS.CAREER_PATH,
        career_path_info: careerPath,
      }),
    );
    dispatch(openPlaylistListStep());
  };

  // ── Dropdown selector ──

  const currentCategoryId = careerPath.career_category_id.toString();
  const [category, setCategory] = useState(currentCategoryId);
  const [path, setPath] = useState(careerPath.name);

  const filteredCareerPathSuggestions =
    careerPathSuggestions?.filter((suggestion) => {
      const selectedCategory = careerCategories.find((cat) => cat.id.toString() === category);

      return selectedCategory
        ? suggestion.careerPath.career_category_id === selectedCategory.id
        : true;
    }) || [];

  const handleCategoryChange = (categoryId: string) => {
    setCategory(categoryId);
    const selectedCategory = careerCategories.find((cat) => cat.id.toString() === categoryId);
    const pathsInNewCategory = selectedCategory
      ? careerPaths.filter((p) => p.career_category_id === selectedCategory.id)
      : [];

    if (pathsInNewCategory.length > 0) {
      const firstPath = pathsInNewCategory[0];

      setPath(firstPath.name);

      if (isShared) {
        navigate(
          `${APP_ROUTE.SHARED_CAREER_PATHS.replace(':uuid', sharedSessionUUID || '')}/${firstPath.id}`,
        );
      } else {
        navigate(`${APP_ROUTE.CAREER_PATHS}/${firstPath.id}`);
      }
    }
  };

  const handlePathChange = (pathName: string) => {
    setPath(pathName);
    const selectedPath = careerPaths.find((p) => p.name === pathName);

    if (selectedPath) {
      if (isShared) {
        navigate(
          `${APP_ROUTE.SHARED_CAREER_PATHS.replace(':uuid', sharedSessionUUID || '')}/${selectedPath.id}`,
        );
      } else {
        navigate(`${APP_ROUTE.CAREER_PATHS}/${selectedPath.id}`);
      }
    }
  };

  const strongFitCount = getStrongFitCountForCategory(
    category,
    careerPathSuggestions,
    careerCategories,
  );

  const dropdownSections: DoubleSelectDropdownProps['sections'] = [
    {
      key: 'left',
      value: category,
      onChange: handleCategoryChange,
      options: transformCareerCategories(careerCategories, careerPathSuggestions),
      placeholder: category,
      badgeCount: strongFitCount,
    },
    {
      key: 'right',
      value: path,
      onChange: handlePathChange,
      options: transformCareerPaths(filteredCareerPathSuggestions),
      placeholder: path,
    },
  ];

  // ── Header config ──

  const careerPathCategoryName = getCareerPathCategoryName(careerCategories, careerPath);
  const careerPathIcon = getCareerPathIcon(careerCategories, careerPath);

  const careerPathSuggestion = careerPathSuggestions
    ? findCareerPathSuggestionById(careerPath.id, careerPathSuggestions)
    : null;
  const careerPathFitColor = getCareerPathFitColor(careerPathSuggestion?.scoreSection || 'LOW');
  const careerPathFitLabel = careerPathSuggestion?.scoreSection
    ? FIT_SCORE_LEVEL_MAP[careerPathSuggestion.scoreSection]
    : 'Low Fit';

  // ── Resource sections (Resources, Opportunities, Scholarships) ──

  const FILTER_BUTTONS = STRINGS.resources.filterButtons;
  const resourceCategories = Object.values(SEARCH_PARENT_CATEGORIES_DEFAULT)
    .filter((category) => !category.isInMoreFilters)
    .filter((category) => category.name !== 'People')
    .map((category) => {
      if (category.name === 'Opportunities') {
        return {
          ...category,
          categories: category.categories.filter(
            (subCategory) => subCategory.key !== 'CAREER_PATH',
          ),
        };
      }

      return category;
    });

  const searchState = useAppSelector((state) => state.careerPathSearch);

  const resourceSections: ResourcePageSection[] = isShared
    ? []
    : (resourceCategories
        .map((category) => {
          const sectionConfig =
            FILTER_BUTTONS[category.name.toLowerCase() as keyof typeof FILTER_BUTTONS];

          if (!sectionConfig) return null;

          const sectionSearchParams = searchState.sections[category.name];
          const searchComplete = sectionSearchParams?.initialResultCount !== undefined;
          const hasResults = !searchComplete || (sectionSearchParams?.initialResultCount ?? 0) > 0;

          return {
            id: sectionConfig.title.toLowerCase(),
            title: sectionConfig.title,
            hideFromMenu: true,
            visible: hasResults,
            headerRightComponents: [
              <ResourceNuggetSectionFilter
                key={`${category.name}-filter`}
                careerPathName={careerPath.name}
                category={category}
              />,
            ],
            component: (
              <ResourceNuggetSection
                careerPath={careerPath}
                category={category}
                sectionTitle={sectionConfig.title}
              />
            ),
          };
        })
        .filter(Boolean) as ResourcePageSection[]);

  // ── Sections ──

  const PAGE_SECTIONS: ResourcePageSection[] = [
    {
      id: 'description',
      title: 'Description',
      hideFromMenu: true,
      hideHeader: true,
      component: (
        <CareerPathOverview
          careerPath={careerPath}
          earlyCareerHires={careerPath.early_career_hires_per_year}
          careerPathSuggestions={careerPathSuggestions}
          responseSetId={responseSetId}
          isShared={isShared}
        />
      ),
    },
    {
      id: 'earning-potential',
      title: STRINGS.earningPotential.earningPotential,
      description: STRINGS.earningPotential.description,
      headerRightComponents: [
        <LubavButton
          key="career-path-earning-potential-lubav-btn"
          id="career-path-earning-potential-lubav-btn"
          label="Explain this to me"
          prompt={`Help me understand the data in the Earning Potential section for ${careerPath.name}. Don't just repeat the numbers — explain the key takeaways.`}
          allowTools={false}
          useGeneralKnowledge
        />,
      ],
      component: (
        <EarningPotential
          careerPathCompensations={careerPathCompensations}
          careerPathSampleJobs={careerPathSampleJobs}
          careerPathName={careerPath.name}
        />
      ),
    },
    {
      id: 'ai-impact',
      title: STRINGS.aiImpact.impactOfAi,
      description: STRINGS.aiImpact.description,
      headerRightComponents: [
        <LubavButton
          key="career-path-ai-impact-lubav-btn"
          id="career-path-ai-impact-lubav-btn"
          label="Compared to other career paths"
          prompt={`Use the search tool to find career paths related to ${careerPath.name}. Then give a short summary like "Compared to related career paths like X, Y, and Z, ${careerPath.name} is..." Touch on the individual components like automation risk, integration intensity, leverage upside, etc. Keep it conversational — not a wall of text.`}
          allowTools
        />,
      ],
      component: (
        <AiImpact aiImpact={careerPath.ai_impact} careerPathAIDisruption={careerPathAIDisruption} />
      ),
    },
    {
      id: 'career-tracks',
      title: STRINGS.trackDistribution.careerTracks,
      description: STRINGS.trackDistribution.sectionDescription,
      headerRightComponents: [
        <LubavButton
          key="career-path-career-tracks-lubav-btn"
          id="career-path-career-tracks-lubav-btn"
          label="Explain this to me"
          prompt={`Explain the Career Tracks distribution for ${careerPath.name}. Briefly describe what each track means in this field and what the overall distribution tells us. Keep it concise.`}
          allowTools={false}
          useGeneralKnowledge
        />,
      ],
      component: (
        <TrackDistribution careerTrackDistribution={careerPath.career_track_distribution} />
      ),
    },
    ...resourceSections,
  ];

  return (
    <ResourceWrapper
      header={
        <ResourceHeader
          title={careerPath.name}
          titleExtra={
            <Chip
              id="career-path-detail-fit-chip"
              variant="badge"
              label={careerPathFitLabel}
              size="sm"
              icon={
                <AppIcon
                  iconName="Circle"
                  diameter="8px"
                  color={careerPathFitColor}
                  fill={careerPathFitColor}
                />
              }
              type="leading"
              sx={fitChipSx}
            />
          }
          subtitle={careerPathCategoryName}
          banner_media_url={careerPath.media_url}
          titleIcon={{ icon: careerPathIcon, backgroundColor: 'Brand/400' }}
          buttonGroup={[
            {
              id: 'career-path-detail-bookmark-button',
              onClick: handleBookmark,
              icon: bookmarkId ? 'BookmarkCheck' : 'Bookmark',
              tooltipHoverText: commonAppStrings.modules.actionTabs.bookmark,
            },
            {
              id: 'career-path-detail-share-button',
              onClick: handleShare,
              icon: 'Share06',
              tooltipOnClickText: 'Copied!',
              tooltipHoverText: commonAppStrings.modules.actionTabs.share,
            },
            {
              id: 'career-path-detail-create-task-button',
              onClick: handleCreateTask,
              icon: 'BookmarkAdd',
              tooltipHoverText: commonAppStrings.modules.actionTabs.task,
            },
          ]}
          headerActions={
            <DoubleSelectDropdown id="career-path-detail-dropdown" sections={dropdownSections} />
          }
          headerActionsSxOverride={dropdownMobileOverrideSx}
        />
      }
      sections={PAGE_SECTIONS}
      showTabs={false}
      chipLabel={careerPath.name}
    />
  );
}

const dropdownMobileOverrideSx: SxProps<Theme> = {
  order: -1,
  width: '100%',
  '@container (min-width: 600px)': {
    order: 0,
    width: 'auto',
  },
};

const fitChipSx: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiChip-icon': {
    marginLeft: backrs.spacing['spacing-none'],
    marginRight: backrs.spacing['spacing-none'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
});
